import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  X,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth, useToken } from '@/store/hooks';

interface ComplaintCategory {
  id: string;
  name: string;
  code: string;
  description?: string;
  type: 'complaint' | 'enquiry' | 'suggestion' | 'maintenance';
  priority_level: number;
  estimated_resolution_time_hours: number;
  requires_approval: boolean;
  is_active: boolean;
  required_fields?: any;
  auto_assignment_rules?: any;
  created_at: string;
  updated_at: string;
  departments?: Array<{
    id: string;
    name: string;
    code: string;
  }>;
}

interface Department {
  id: string;
  name: string;
  code: string;
}

interface FormData {
  name: string;
  description: string;
  department_ids: string[];
  type: 'complaint' | 'enquiry' | 'suggestion' | 'maintenance';
  priority_level: number;
  estimated_resolution_time_hours: number;
  requires_approval: boolean;
  is_active: boolean;
}

interface FormErrors {
  name?: string;
  department_ids?: string;
  type?: string;
  priority_level?: string;
  estimated_resolution_time_hours?: string;
  general?: string;
}

const CategoriesPage = () => {
  const { user } = useAuth();
  const token = useToken();
  const [categories, setCategories] = useState<ComplaintCategory[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<ComplaintCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingCategory, setEditingCategory] = useState<ComplaintCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    department_ids: [],
    type: 'complaint',
    priority_level: 5,
    estimated_resolution_time_hours: 24,
    requires_approval: false,
    is_active: true
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Check if user is universal admin
  const isUniversalAdmin = user?.role === 'universal_admin';

  useEffect(() => {
    if (isUniversalAdmin) {
      fetchCategories();
      fetchDepartments();
    }
  }, [isUniversalAdmin]);

  useEffect(() => {
    filterCategories();
  }, [categories, searchTerm, typeFilter, statusFilter]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/categories', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/v1/departments', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDepartments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const filterCategories = () => {
    let filtered = categories;

    if (searchTerm) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(category => category.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(category => 
        statusFilter === 'active' ? category.is_active : !category.is_active
      );
    }

    setFilteredCategories(filtered);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.department_ids || formData.department_ids.length === 0) {
      newErrors.department_ids = 'At least one department is required';
    }

    if (formData.priority_level < 1 || formData.priority_level > 10) {
      newErrors.priority_level = 'Priority level must be between 1 and 10';
    }

    if (formData.estimated_resolution_time_hours < 1) {
      newErrors.estimated_resolution_time_hours = 'Estimated resolution time must be at least 1 hour';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleDepartmentToggle = (departmentId: string) => {
    setFormData(prev => {
      const prevIds = Array.isArray(prev.department_ids) ? prev.department_ids : [];
      const isCurrentlySelected = prevIds.includes(departmentId);
      
      if (isCurrentlySelected) {
        // Remove department
        return {
          ...prev,
          department_ids: prevIds.filter(id => id !== departmentId)
        };
      } else {
        // Add department
        return {
          ...prev,
          department_ids: [...prevIds, departmentId]
        };
      }
    });
    
    // Clear error when user changes selection
    if (errors.department_ids) {
      setErrors(prev => ({
        ...prev,
        department_ids: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const url = modalMode === 'create' 
        ? '/api/v1/categories' 
        : `/api/v1/categories/${editingCategory?.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      if (!token) {
        setErrors({
          general: 'Authentication required. Please login again.'
        });
        setIsSubmitting(false);
        return;
      }

      // Prepare data for submission - ensure correct types
      const departmentIds = Array.isArray(formData.department_ids) 
        ? formData.department_ids.filter(id => id && id.trim() !== '') 
        : [];
      
      if (departmentIds.length === 0) {
        setErrors({
          department_ids: 'At least one department is required',
          general: 'Please select at least one department'
        });
        setIsSubmitting(false);
        return;
      }

      const submitData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        department_ids: departmentIds,
        type: formData.type,
        priority_level: parseInt(String(formData.priority_level), 10),
        estimated_resolution_time_hours: parseInt(String(formData.estimated_resolution_time_hours), 10),
        requires_approval: Boolean(formData.requires_approval),
        is_active: Boolean(formData.is_active !== undefined ? formData.is_active : true)
      };

      // Ensure department_ids is a non-empty array
      if (!Array.isArray(submitData.department_ids) || submitData.department_ids.length === 0) {
        setErrors({
          department_ids: 'At least one department is required',
          general: 'Please select at least one department'
        });
        setIsSubmitting(false);
        return;
      }

      // Validate each department ID is a valid UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const invalidIds = submitData.department_ids.filter(id => !uuidRegex.test(id));
      if (invalidIds.length > 0) {
        setErrors({
          department_ids: 'Invalid department ID format',
          general: 'One or more department IDs are invalid'
        });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        setErrors({
          general: 'Failed to parse server response. Please try again.'
        });
        setIsSubmitting(false);
        return;
      }

      // Log the response for debugging
      if (!response.ok) {
        console.error('Backend validation errors:', responseData);
      }

      if (response.ok) {
        if (modalMode === 'create') {
          setCategories(prev => [...prev, responseData.data]);
        } else {
          setCategories(prev => prev.map(cat => 
            cat.id === editingCategory?.id ? responseData.data : cat
          ));
        }
        
        closeModal();
      } else {
        // Handle validation errors
        const newErrors: FormErrors = {};
        
        if (responseData.errors && Array.isArray(responseData.errors)) {
          // Map backend validation errors to form errors
          responseData.errors.forEach((error: any) => {
            const field = error.param || error.field;
            if (field === 'department_ids' || field === 'department_ids.*') {
              newErrors.department_ids = error.msg || 'Please select at least one department';
            } else if (field === 'name') {
              newErrors.name = error.msg || 'Name is required';
            } else if (field === 'type') {
              newErrors.type = error.msg || 'Valid type is required';
            } else if (field === 'priority_level') {
              newErrors.priority_level = error.msg || 'Priority level must be between 1 and 10';
            } else if (field === 'estimated_resolution_time_hours') {
              newErrors.estimated_resolution_time_hours = error.msg || 'Resolution time must be a positive number';
            }
          });
        }
        
        // Set general error if no specific field errors
        if (Object.keys(newErrors).length === 0) {
          newErrors.general = responseData.message || 'Failed to save category';
        } else {
          newErrors.general = responseData.message || 'Please fix the errors below';
        }
        
        setErrors(newErrors);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setErrors({
        general: 'Network error. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (category: ComplaintCategory) => {
    setEditingCategory(category);
    setModalMode('edit');
    setFormData({
      name: category.name,
      description: category.description || '',
      department_ids: category.departments?.map(d => d.id) || [],
      type: category.type,
      priority_level: category.priority_level,
      estimated_resolution_time_hours: category.estimated_resolution_time_hours,
      requires_approval: category.requires_approval,
      is_active: category.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Network error. Please try again.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setModalMode('create');
    setFormData({
      name: '',
      description: '',
      department_ids: [],
      type: 'complaint',
      priority_level: 5,
      estimated_resolution_time_hours: 24,
      requires_approval: false,
      is_active: true
    });
    setErrors({});
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'complaint': return 'bg-red-100 text-red-800';
      case 'enquiry': return 'bg-blue-100 text-blue-800';
      case 'suggestion': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (level: number) => {
    if (level <= 3) return 'bg-red-100 text-red-800';
    if (level <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (!isUniversalAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Header Section */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <Settings className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">Categories</h1>
                <p className="text-sm text-gray-600">Organize complaint categories and departments</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchCategories}
                className="inline-flex items-center px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => {
                  setModalMode('create');
                  setEditingCategory(null);
                  setShowModal(true);
                }}
                className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Category
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 mb-6">
          <div className="p-5 lg:p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center">
                <Filter className="h-4 w-4 text-purple-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">Search & Filter</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Search Categories</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type to search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 w-full h-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Category Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full h-10 px-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-gray-50 focus:bg-white text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="complaint">Complaint</option>
                  <option value="enquiry">Enquiry</option>
                  <option value="suggestion">Suggestion</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full h-10 px-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-gray-50 focus:bg-white text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            {(searchTerm || typeFilter !== 'all' || statusFilter !== 'all') && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setTypeFilter('all');
                    setStatusFilter('all');
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <X className="h-3 w-3 mr-1.5" />
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Categories Grid */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                All Categories
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Showing {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-14 w-14 border-4 border-purple-100 border-t-purple-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-500 font-medium">Loading categories...</p>
              </div>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-16 text-center">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-5">
                <Settings className="h-10 w-10 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No categories found</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Create your first category to organize complaints and departments.'}
              </p>
              {(!searchTerm && typeFilter === 'all' && statusFilter === 'all') && (
                <button
                  onClick={() => {
                    setModalMode('create');
                    setEditingCategory(null);
                    setShowModal(true);
                  }}
                  className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Category
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
              {filteredCategories.map((category, index) => {
                const colors = [
                  'from-purple-500 to-pink-500',
                  'from-blue-500 to-cyan-500',
                  'from-green-500 to-emerald-500',
                  'from-orange-500 to-red-500',
                  'from-indigo-500 to-purple-500',
                  'from-pink-500 to-rose-500'
                ];
                const colorIndex = index % colors.length;
                
                return (
                  <div
                    key={category.id}
                    className="group bg-white rounded-2xl shadow-md border-2 border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-5">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${colors[colorIndex]} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                              <Settings className="h-5 w-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-bold text-gray-900 truncate">
                                {category.name}
                              </h3>
                              <p className="text-xs font-mono text-gray-400">
                                {category.code}
                              </p>
                            </div>
                          </div>
                          {category.description && (
                            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed mt-2">
                              {category.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Departments */}
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-600 mb-2">Departments</div>
                        {category.departments && category.departments.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {category.departments.map((dept) => (
                              <span
                                key={dept.id}
                                className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                              >
                                {dept.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No departments assigned</span>
                        )}
                      </div>

                      {/* Metadata Grid */}
                      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1.5">Type</div>
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getTypeColor(category.type)}`}>
                            {category.type}
                          </span>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1.5">Priority</div>
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPriorityColor(category.priority_level)}`}>
                            Level {category.priority_level}
                          </span>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1.5">Resolution</div>
                          <div className="flex items-center text-sm text-gray-900 font-medium">
                            <Clock className="h-3.5 w-3.5 mr-1 text-gray-400" />
                            {category.estimated_resolution_time_hours}h
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1.5">Status</div>
                          <div className="flex items-center">
                            {category.is_active ? (
                              <>
                                <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                                <span className="text-sm text-green-600 font-semibold">Active</span>
                              </>
                            ) : (
                              <>
                                <X className="h-4 w-4 text-red-500 mr-1" />
                                <span className="text-sm text-red-600 font-semibold">Inactive</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Footer Badges */}
                      {category.requires_approval && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            ⚠️ Requires Approval
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Category Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-semibold text-gray-900">
                {modalMode === 'create' ? 'Create New Category' : 'Edit Category'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{errors.general}</p>
                  </div>
                </div>
              )}

              {/* Category Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter category name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                  placeholder="Enter category description (optional)"
                />
              </div>

              {/* Departments Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departments <span className="text-red-500">*</span>
                  <span className="text-gray-500 text-xs font-normal ml-2">(Select one or more)</span>
                </label>
                <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50">
                  {departments.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No departments available</p>
                  ) : (
                    <div className="space-y-2">
                      {departments.map((dept) => {
                        const isSelected = formData.department_ids.includes(dept.id);
                        return (
                          <div
                            key={dept.id}
                            onClick={() => handleDepartmentToggle(dept.id)}
                            className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'bg-blue-50 border-blue-500 shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center flex-1">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 transition-all ${
                                isSelected
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'bg-white border-gray-300'
                              }`}>
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <span className={`text-sm font-medium ${
                                isSelected ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {dept.name}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {errors.department_ids && (
                  <p className="mt-2 text-sm text-red-600">{errors.department_ids}</p>
                )}
                <div className="mt-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-blue-600">{formData.department_ids.length}</span>
                    {' '}department{formData.department_ids.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              </div>

              {/* Type and Settings Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Category Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  >
                    <option value="complaint">Complaint</option>
                    <option value="enquiry">Enquiry</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                {/* Priority Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority Level <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority_level}
                    onChange={(e) => handleInputChange('priority_level', parseInt(e.target.value) || 1)}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.priority_level ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.priority_level && (
                    <p className="mt-1 text-sm text-red-600">{errors.priority_level}</p>
                  )}
                </div>
              </div>

              {/* Resolution Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Resolution Time (hours) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.estimated_resolution_time_hours}
                  onChange={(e) => handleInputChange('estimated_resolution_time_hours', parseInt(e.target.value) || 1)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.estimated_resolution_time_hours ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.estimated_resolution_time_hours && (
                  <p className="mt-1 text-sm text-red-600">{errors.estimated_resolution_time_hours}</p>
                )}
              </div>

              {/* Checkboxes */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.requires_approval}
                    onChange={(e) => handleInputChange('requires_approval', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Requires Approval
                  </span>
                </label>

                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Active
                  </span>
                </label>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {modalMode === 'create' ? 'Create Category' : 'Update Category'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;